import type { IconGlyph } from '@sunwebgroup/uds-ui/icon'
import type { IntlShape } from 'react-intl'
import type {
  FlightInformation,
  FlightLeg,
  FlightLuggageItem,
  FlightLuggageSize,
  FlightLuggageType,
} from '../../domain/types'

export const FLIGHT_LUGGAGE_DESCRIPTION_KEY = {
  checkedWeight: 'booking_details_flight_luggage_checked_weight',
  cabinLarge: 'booking_details_flight_luggage_cabin_large',
  cabinSmall: 'booking_details_flight_luggage_cabin_small',
  cabinGeneric: 'booking_details_flight_luggage_cabin_generic',
}

export const FLIGHT_LUGGAGE_EMPTY_MESSAGE_KEY = 'booking_details_flight_luggage_empty'

export const FLIGHT_LUGGAGE_DIRECTION_KEY = {
  inbound: 'booking_details_flight_inbound',
  outbound: 'booking_details_flight_outbound',
}

type FlightDirection = keyof typeof FLIGHT_LUGGAGE_DIRECTION_KEY

export interface FlightLuggageCounts {
  Checked: Partial<Record<FlightLuggageSize, number>>
  Cabin: Partial<Record<FlightLuggageSize, number>>
}

export interface FlightLuggageDisplayItem {
  glyph: IconGlyph
  description: string
}

export interface FlightLegsLuggageDisplayItem {
  glyph: IconGlyph
  descriptions: string[]
}

const ROUTE_DESCRIPTIONS_SEPARATOR = ' · '

interface GroupedFlightLuggage {
  checked: FlightLuggageItem[]
  cabinLarge: FlightLuggageItem[]
  cabinSmallCount: number
  cabinNotSetCount: number
}

function isValidLuggageType(type: FlightLuggageType | ''): type is FlightLuggageType {
  return type === 'Checked' || type === 'Cabin'
}

function isValidLuggageSize(size: FlightLuggageSize | ''): size is FlightLuggageSize {
  return size === 'Large' || size === 'Small' || size === 'NotSet'
}

function hasValidLuggageWeight(weightInKg: string): boolean {
  if (!weightInKg) {
    return false
  }

  const weight = Number(weightInKg)
  return !Number.isNaN(weight) && weight > 0
}

function hasTextContent(value: string): boolean {
  return value.trim().length > 0
}

const CABIN_LUGGAGE_BY_SIZE: Record<
  FlightLuggageSize,
  (grouped: GroupedFlightLuggage, item: FlightLuggageItem, count: number) => void
> = {
  Large: (grouped, item) => grouped.cabinLarge.push(item),
  Small: (grouped, _item, count) => { grouped.cabinSmallCount += count },
  NotSet: (grouped, _item, count) => { grouped.cabinNotSetCount += count },
}

const LUGGAGE_GROUP_BY_TYPE: Record<
  FlightLuggageType,
  (grouped: GroupedFlightLuggage, item: FlightLuggageItem, count: number) => void
> = {
  Checked: (grouped, item) => grouped.checked.push(item),
  Cabin: (grouped, item, count) => {
    if (!isValidLuggageSize(item.size)) {
      return
    }

    CABIN_LUGGAGE_BY_SIZE[item.size](grouped, item, count)
  },
}

function sumParticipantCountsByKey(
  items: FlightLuggageItem[],
  getKey: (item: FlightLuggageItem) => string,
): Map<string, number> {
  const countsByKey = new Map<string, number>()

  for (const item of items) {
    const key = getKey(item)
    countsByKey.set(key, (countsByKey.get(key) ?? 0) + item.participantIds.length)
  }

  return countsByKey
}

function buildWeightGroupedDescription(
  items: FlightLuggageItem[],
  formatMessage: IntlShape['formatMessage'],
  withWeightKey: (typeof FLIGHT_LUGGAGE_DESCRIPTION_KEY)[keyof typeof FLIGHT_LUGGAGE_DESCRIPTION_KEY],
  fallbackKey?: (typeof FLIGHT_LUGGAGE_DESCRIPTION_KEY)[keyof typeof FLIGHT_LUGGAGE_DESCRIPTION_KEY],
): string {
  const parts: string[] = []

  const itemsWithWeight = items.filter(item => hasValidLuggageWeight(item.weightInKg))
  for (const [weight, count] of Array.from(sumParticipantCountsByKey(itemsWithWeight, item => item.weightInKg))) {
    parts.push(formatMessage({ id: withWeightKey }, { count, weight }))
  }

  const itemsWithoutWeight = items.filter(
    item => !hasValidLuggageWeight(item.weightInKg) && hasTextContent(item.description),
  )
  for (const [description, count] of Array.from(sumParticipantCountsByKey(itemsWithoutWeight, item => item.description.trim()))) {
    parts.push(`${count}x ${description}`)
  }

  if (fallbackKey) {
    const itemsWithoutWeightOrDescription = items.filter(
      item => !hasValidLuggageWeight(item.weightInKg) && !hasTextContent(item.description),
    )
    const fallbackCount = itemsWithoutWeightOrDescription.reduce(
      (total, item) => total + item.participantIds.length,
      0,
    )
    if (fallbackCount > 0) {
      parts.push(formatMessage({ id: fallbackKey }, { count: fallbackCount }))
    }
  }

  return parts.join(' + ')
}

export function aggregateFlightLuggageCounts(items: FlightLuggageItem[]): FlightLuggageCounts {
  const result: FlightLuggageCounts = { Checked: {}, Cabin: {} }

  for (const { type, size, participantIds } of items) {
    const count = participantIds.length
    if (count === 0 || !isValidLuggageType(type) || !isValidLuggageSize(size)) {
      continue
    }

    result[type][size] = (result[type][size] ?? 0) + count
  }

  return result
}

export function groupFlightLuggage(items: FlightLuggageItem[]): GroupedFlightLuggage {
  const grouped: GroupedFlightLuggage = {
    checked: [],
    cabinLarge: [],
    cabinSmallCount: 0,
    cabinNotSetCount: 0,
  }

  for (const item of items) {
    const count = item.participantIds.length
    if (count === 0) {
      continue
    }

    if (!isValidLuggageType(item.type)) {
      continue
    }

    LUGGAGE_GROUP_BY_TYPE[item.type](grouped, item, count)
  }

  return grouped
}

function hasGroupedFlightLuggageContent(grouped: GroupedFlightLuggage): boolean {
  const hasDisplayableCheckedLuggage = grouped.checked.some(
    item => hasValidLuggageWeight(item.weightInKg) || hasTextContent(item.description),
  )

  return grouped.cabinSmallCount > 0
    || grouped.cabinLarge.length > 0
    || grouped.cabinNotSetCount > 0
    || hasDisplayableCheckedLuggage
}

export function buildFlightLuggageDisplayItems(
  items: FlightLuggageItem[],
  formatMessage: IntlShape['formatMessage'],
): FlightLuggageDisplayItem[] {
  const grouped = groupFlightLuggage(items)

  const result: FlightLuggageDisplayItem[] = []

  if (grouped.cabinSmallCount > 0) {
    result.push({
      glyph: 'Backpack',
      description: formatMessage(
        { id: FLIGHT_LUGGAGE_DESCRIPTION_KEY.cabinSmall },
        { count: grouped.cabinSmallCount },
      ),
    })
  }

  if (grouped.cabinLarge.length > 0) {
    result.push({
      glyph: 'SuitcaseOnWheels',
      description: buildWeightGroupedDescription(
        grouped.cabinLarge,
        formatMessage,
        FLIGHT_LUGGAGE_DESCRIPTION_KEY.cabinLarge,
        FLIGHT_LUGGAGE_DESCRIPTION_KEY.cabinGeneric,
      ),
    })
  }

  if (grouped.cabinNotSetCount > 0) {
    result.push({
      glyph: 'SuitcaseOnWheels',
      description: formatMessage(
        { id: FLIGHT_LUGGAGE_DESCRIPTION_KEY.cabinGeneric },
        { count: grouped.cabinNotSetCount },
      ),
    })
  }

  if (grouped.checked.length > 0) {
    const checkedDescription = buildWeightGroupedDescription(
      grouped.checked,
      formatMessage,
      FLIGHT_LUGGAGE_DESCRIPTION_KEY.checkedWeight,
    )
    if (checkedDescription) {
      result.push({
        glyph: 'Suitcase',
        description: checkedDescription,
      })
    }
  }

  return result
}

export function hasFlightLuggageContent(legs: FlightLeg[]): boolean {
  return hasGroupedFlightLuggageContent(groupFlightLuggage(legs.flatMap(leg => leg.luggage)))
}

type DescribeLuggage = (luggage: FlightLuggageItem[]) => string

function getLegRoute(leg: FlightLeg): string {
  const departure = leg.departureAirportCode || leg.departureAirportName
  const arrival = leg.arrivalAirportCode || leg.arrivalAirportName
  return `${departure}–${arrival}`
}

function hasSameLuggageOnEveryLeg(legs: FlightLeg[], describeLuggage: DescribeLuggage): boolean {
  const legDescriptions = legs.map(leg => describeLuggage(leg.luggage))
  return new Set(legDescriptions).size === 1
}

function groupLuggageByRoute(legs: FlightLeg[]): Map<string, FlightLuggageItem[]> {
  const luggageByRoute = new Map<string, FlightLuggageItem[]>()

  for (const leg of legs) {
    const route = getLegRoute(leg)
    luggageByRoute.set(route, [...(luggageByRoute.get(route) ?? []), ...leg.luggage])
  }

  return luggageByRoute
}

function describeJourneysLuggage(
  journeys: FlightLeg[][],
  describeLuggage: DescribeLuggage,
  routeSeparator: string,
): string[] {
  const journeysWithLuggage = journeys
    .map(legs => legs.filter(leg => describeLuggage(leg.luggage)))
    .filter(legs => legs.length > 0)

  if (!journeysWithLuggage.length) {
    return []
  }

  const isSameOnEveryLeg = journeysWithLuggage.every(legs => hasSameLuggageOnEveryLeg(legs, describeLuggage))

  if (isSameOnEveryLeg) {
    const luggageOfEachJourney = journeysWithLuggage.flatMap(([firstLeg]) => firstLeg.luggage)
    return [describeLuggage(luggageOfEachJourney)]
  }

  const luggageByRoute = groupLuggageByRoute(journeysWithLuggage.flat())
  return Array.from(luggageByRoute, ([route, luggage]) => `${route}${routeSeparator}${describeLuggage(luggage)}`)
}

const FLIGHT_LUGGAGE_ROWS: { glyph: IconGlyph, includes: (item: FlightLuggageItem) => boolean }[] = [
  { glyph: 'Backpack', includes: item => item.type === 'Cabin' && item.size === 'Small' },
  { glyph: 'SuitcaseOnWheels', includes: item => item.type === 'Cabin' && item.size === 'Large' },
  { glyph: 'SuitcaseOnWheels', includes: item => item.type === 'Cabin' && item.size === 'NotSet' },
  { glyph: 'Suitcase', includes: item => item.type === 'Checked' },
]

function describeLuggageRow(rowLuggage: FlightLuggageItem[], formatMessage: IntlShape['formatMessage']): string {
  const [row] = buildFlightLuggageDisplayItems(rowLuggage, formatMessage)
  return row?.description ?? ''
}

export function buildFlightLegsLuggageDisplayItems(
  legs: FlightLeg[],
  formatMessage: IntlShape['formatMessage'],
): FlightLegsLuggageDisplayItem[] {
  const displayItems: FlightLegsLuggageDisplayItem[] = []

  for (const { glyph, includes } of FLIGHT_LUGGAGE_ROWS) {
    const describeRow = (luggage: FlightLuggageItem[]) => describeLuggageRow(luggage.filter(includes), formatMessage)
    const descriptions = describeJourneysLuggage([legs], describeRow, ': ')

    if (descriptions.length) {
      displayItems.push({ glyph, descriptions })
    }
  }

  return displayItems
}

function buildDirectionLine(
  direction: FlightDirection,
  formatMessage: IntlShape['formatMessage'],
  content: string,
): string {
  return `${formatMessage({ id: FLIGHT_LUGGAGE_DIRECTION_KEY[direction] })}: ${content}`
}

function isCheckedLuggageWithParticipants(item: FlightLuggageItem): boolean {
  return item.type === 'Checked' && item.participantIds.length > 0
}

export function buildCheckedLuggageDescriptions(
  flightInformation: FlightInformation[] | null,
  formatMessage: IntlShape['formatMessage'],
): string[] {
  const describeCheckedLuggage = (luggage: FlightLuggageItem[]) => buildWeightGroupedDescription(
    luggage.filter(isCheckedLuggageWithParticipants),
    formatMessage,
    FLIGHT_LUGGAGE_DESCRIPTION_KEY.checkedWeight,
  )
  const lines: string[] = []

  for (const direction of ['outbound', 'inbound'] as const) {
    const journeys = (flightInformation ?? []).map(flight => flight[direction] ?? [])
    const descriptions = describeJourneysLuggage(journeys, describeCheckedLuggage, ' ')

    if (descriptions.length) {
      lines.push(buildDirectionLine(direction, formatMessage, descriptions.join(ROUTE_DESCRIPTIONS_SEPARATOR)))
    }
  }

  return lines
}

function keepLuggageOfParticipant(legs: FlightLeg[] | null, participantId: string): FlightLeg[] | null {
  if (!legs) {
    return null
  }

  return legs.map(leg => ({
    ...leg,
    luggage: leg.luggage.map(item => ({
      ...item,
      participantIds: item.participantIds.filter(id => String(id) === participantId),
    })),
  }))
}

export function buildParticipantCheckedLuggageDescriptions(
  flightInformation: FlightInformation[] | null,
  participantId: string,
  formatMessage: IntlShape['formatMessage'],
): string[] {
  const participantFlightInformation = (flightInformation ?? []).map(flight => ({
    ...flight,
    outbound: keepLuggageOfParticipant(flight.outbound, participantId),
    inbound: keepLuggageOfParticipant(flight.inbound, participantId),
  }))

  return buildCheckedLuggageDescriptions(participantFlightInformation, formatMessage)
}
